import type { Child } from '../../api/models';
import { AppCard } from '../../components/AppCard';
import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { Strings } from '../../strings';

import { isEmpty } from './ChildrenState';
import { useChildren } from './useChildren';

export function ChildrenScreen({
  onEnteredChildMode,
  onAddChild,
  onOpenDashboard,
}: {
  onEnteredChildMode: (child: Child) => void;
  onAddChild: () => void;
  onOpenDashboard: (childId: string) => void;
}) {
  const model = useChildren(onEnteredChildMode);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <AppText as="h1" style="titleL">
        {Strings.navChildren}
      </AppText>

      {model.state.error !== null ? (
        <RetryNotice
          text={model.state.error}
          correlationId={model.state.errorCorrelationId}
          tone="error"
          onRetry={() => void model.load()}
        />
      ) : null}

      {isEmpty(model.state) ? (
        <AppCard>
          <AppText style="titleS">{Strings.childrenEmptyTitle}</AppText>
          <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
            {Strings.childrenEmptyBody}
          </AppText>
        </AppCard>
      ) : null}

      {model.state.children.map((child: Child) => (
        <div
          key={child.id}
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-l)',
            padding: 'var(--space-4)',
          }}
        >
          {/*
            Tapping the card enters child mode — the common action, and the one a parent handing over a
            device is reaching for. The dashboard is a separate, explicitly labelled tap.

            Two sibling buttons rather than one inside the other: a button inside a button is invalid HTML
            and the inner one stops being reachable from a keyboard. Same two actions, same layout.
          */}
          <button
            type="button"
            onClick={() => void model.enterChildMode(child)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            <AppText as="span" style="titleM">
              {child.nickname}
            </AppText>
            <AppText style="bodyS" color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
              {Strings.childSummary(child.birthYear, child.schoolGrade)}
            </AppText>
            {child.inCalibration ? (
              <AppText style="bodyS" color="var(--action-primary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.childInCalibration}
              </AppText>
            ) : null}
          </button>

          <button
            type="button"
            onClick={() => onOpenDashboard(child.id)}
            style={{
              marginTop: 'var(--space-2)',
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--action-primary)',
              fontSize: '15px',
              lineHeight: '20px',
              fontWeight: 600,
            }}
          >
            {Strings.actionDetails}
          </button>
        </div>
      ))}

      <PrimaryButton label={Strings.actionAddChild} onClick={onAddChild} height="var(--touch-parent)" />
    </div>
  );
}
